import React from 'react'
import { Card, Row, Col } from 'react-bootstrap'
import {
  formatNumber,
  formatCurrency,
  useWindowSize,
  IsSpecialEmployer,
  payFrequencyToPaysPA
} from 'application/helpers'
import * as E from './estimateDetailElements'
import * as S from './estimateSummarySectionElements'
import * as P from './../pdf/pdfElements'
import globals from 'application/constants/globals'
import { getCurrentResolutionBreakPoint } from 'application/utils/utilityFunctions'
import { EstimateDetailStaticData } from './estimateDetailStaticData'
interface IEstimateSummaryProps {
  annualRate: number
  brokerage: number
  brokeragePerAnnum: number
  carRepaymentPerPayExGST: number
  compInsuranceCommGST: number
  compInsuranceCommissionExGST: number
  compInsuranceCommissionPerAnnumIncGST: number
  employerCode: string
  employerDisplayInterestRateOnDigitalEstimate: boolean
  extendedWarrantyCommExGST: number
  extendedWarrantyCommGST: number
  extendenWarrantyCommissionPerAnnumIncGST: number
  fbtMethod: string
  financeAmount: number
  financierEstablishmentFee: number
  financierEstablishmentFeePerAnnum: number
  gapCoverCommGST: number
  gapCoverCommissionExGST: number
  gapCoverCommissionPerAnnumIncGST: number
  isPAC: boolean
  kmPerAnnum: number
  leaseProtectionCommExGST: number
  leaseProtectionCommGST: number
  leaseProtectionCommissionPerAnnumIncGST: number
  leaseResidualAmountIncGST: number
  leaseTerm: number
  monthlyRepaymentAmountIncGST: number
  nlFeePerPay: number
  nlFeePerPayExGST: number
  payFrequency: string
  pdf?: boolean
  preTaxPerPay:number
  residualPCT: number
  rpcCommGST: number
  rpcCommissionExGST: number
  rpcCommissionPerAnnumIncGST: number
  specm: number
  title: string
  totalLeaseRunningCostsPerPayExGST: number
  vehicleFBTValue: number
  vehicleProcurementFee: number
}

interface ICardItem {
  text: string
  value: number | string
  isCurrency: boolean
  id?: string
}

const EstimateSummarySection = (props: IEstimateSummaryProps) => {
  const isShortLease = props.leaseTerm < 12
  let isSAGBYO = IsSpecialEmployer('SAGBYO')
  let isACTGO =  IsSpecialEmployer('ACTGO')

  let totalLossAssist = 'Total Loss Assist'
  let safeGuardorExtended = 'Safeguard Ultimate Warranty'
  let rpcText = 'Residual Protection Cover'
  if (props.isPAC) {
    totalLossAssist = 'Total Loss Assist Insurance'
    safeGuardorExtended = 'Extended Warranty Insurance'
    rpcText = 'Guaranteed Buy Back Insurance'
  }

  const [width] = useWindowSize()
  const resolutionBreakpoint = {
    nonDesktop: getCurrentResolutionBreakPoint(false),
  }

  //Return a zero value if item undefined, otherwise return item rounded to 2 dp.
  //Can provided two values, which if both are defined & not-null will be added then rounded
  const safeValue = (item: number | undefined, item2?: number | undefined) => {
    if (item === undefined) return 0
    if (!item2) return Math.round(item * 100) / 100
    else return Math.round((item + item2) * 100) / 100
  }

  let procurementFee: number = 0
  let totalCommissionValue: number = 0
  let compMVInsValue: number = 0
  let financierFeeVal: number = 0
  let totalLossAssistVal: number = 0
  let rpcVal: number = 0
  let extendedWarrantyVal: number = 0
  let leaseProtInsVal: number = 0
  let brokerageVal: number = 0

  if (!isShortLease) {
    procurementFee = safeValue(
      props.vehicleProcurementFee && props.vehicleProcurementFee > 0
        ? (props.vehicleProcurementFee / props.leaseTerm) * 12
        : 0
    )

    compMVInsValue = safeValue(props.compInsuranceCommissionPerAnnumIncGST)
    totalLossAssistVal = safeValue(props.gapCoverCommissionPerAnnumIncGST)
    rpcVal = safeValue(props.rpcCommissionPerAnnumIncGST)
    extendedWarrantyVal = safeValue(
      props.extendenWarrantyCommissionPerAnnumIncGST
    )
    leaseProtInsVal = safeValue(props.leaseProtectionCommissionPerAnnumIncGST)
    financierFeeVal = safeValue(props.financierEstablishmentFeePerAnnum)
    brokerageVal = safeValue(props.brokeragePerAnnum)

    if (globals.isBrandMX) {
      //Remove GST from these values for Maxxia. Procurement, financier & brokerage without GST?
      compMVInsValue = compMVInsValue > 0 ? compMVInsValue / 1.1 : 0
      totalLossAssistVal = totalLossAssistVal > 0 ? totalLossAssistVal / 1.1 : 0
      rpcVal = rpcVal > 0 ? rpcVal / 1.1 : 0
      extendedWarrantyVal =
        extendedWarrantyVal > 0 ? extendedWarrantyVal / 1.1 : 0
      leaseProtInsVal = leaseProtInsVal > 0 ? leaseProtInsVal / 1.1 : 0
    }
  } else {
    //Lease less than 12 months, use total values, not annualised
    procurementFee = safeValue(props.vehicleProcurementFee)
    financierFeeVal = safeValue(props.financierEstablishmentFee)
    brokerageVal = safeValue(props.brokerage)

    //Maxxia GST exclusive, RemServ GST inclusive
    compMVInsValue = safeValue(
      props.compInsuranceCommissionExGST,
      globals.isBrandRS ? props.compInsuranceCommGST : 0
    )
    totalLossAssistVal = safeValue(
      props.gapCoverCommissionExGST,
      globals.isBrandRS ? props.gapCoverCommGST : 0
    )
    rpcVal = safeValue(props.rpcCommissionExGST, props.rpcCommGST)
    extendedWarrantyVal = safeValue(
      props.extendedWarrantyCommExGST,
      globals.isBrandRS ? props.extendedWarrantyCommGST : 0
    )
    leaseProtInsVal = safeValue(
      props.leaseProtectionCommExGST,
      globals.isBrandRS ? props.leaseProtectionCommGST : 0
    )
  }

  totalCommissionValue =
    compMVInsValue +
    totalLossAssistVal +
    rpcVal +
    extendedWarrantyVal +
    leaseProtInsVal
  if (props.employerCode === 'NSWHN' || isACTGO || props.employerCode === 'QG')
    totalCommissionValue =
      totalCommissionValue + financierFeeVal + procurementFee + brokerageVal

  const RowEntry = (props: {
    id: string
    title: string
    isCurrency: boolean
    value: any
    boldValue?: boolean
    boldText?: boolean
  }) => {
    let returnValue = <></>

    if (
      (props.isCurrency && props.value === 0) ||
      (!props.isCurrency && props.value === null)
    )
      returnValue = <></>
    else {
      if (width >= resolutionBreakpoint.nonDesktop)
        returnValue = (
          <Row>
            <S.RowEntryText xs={7} md={9} boldText={props.boldText}>
              {props.title}
            </S.RowEntryText>
            <S.RowEntryValue
              xs={5}
              md={3}
              id={props.id + '.value'}
              boldValue={props.boldValue}
              right
            >
              {props.isCurrency ? formatCurrency(props.value) : props.value}
            </S.RowEntryValue>
          </Row>
        )

      if (width < resolutionBreakpoint.nonDesktop)
        returnValue = (
          <>
            <Row>
              <S.RowEntryText xs={12} boldText={props.boldText}>
                <div id={props.id + '.title'}>{props.title}</div>
              </S.RowEntryText>
            </Row>
            <Row className="mb-3">
              <S.RowEntryValue
                xs={12}
                id={props.id + '.value'}
                boldValue={props.boldValue}
              >
                {props.isCurrency ? formatCurrency(props.value) : props.value}
              </S.RowEntryValue>
            </Row>
          </>
        )
    }

    return returnValue
  }

  let cards: ICardItem[] = []
  let commissions: ICardItem[] = []
  if (globals.isBrandRS) {
    cards.push(
      {
        text: 'Total Finance Amount',
        value: props.financeAmount,
        isCurrency: true,
      },
      {
          text: 'Finance Rate',
          value: `${props.annualRate}%`,
          isCurrency: false,
      })
      if (!IsSpecialEmployer('REMEQ')) 
        cards.push(
        {
          text: 'Supplier Margin',
          value: props.brokerage,
          isCurrency: true
        })  
      cards.push(
      {
        text: 'Residual Percentage',
        value: `${props.residualPCT}%`,
        isCurrency: false,
      },
      {
        text: 'Residual Value (Inc GST)',
        value: props.leaseResidualAmountIncGST,
        isCurrency: true,
      },
      {
        text: 'Vehicle Lease Repayments (per pay) (ex GST)',
        value: props.carRepaymentPerPayExGST,
        isCurrency: true,
      },
      {
        text: '',
        value: '',
        isCurrency: false,
      })
      if (!IsSpecialEmployer('REMEQ')) //Always zero for REMEQ so suppressed
        cards.push({
          text: 'Novated Lease Admin Fee (per pay) (ex GST)',
          value: props.nlFeePerPayExGST,
          isCurrency: true,
        })
      cards.push({
        text: 'Total lease and running costs (per pay) (ex GST)',
        value: props.totalLeaseRunningCostsPerPayExGST,
        isCurrency: true,
      },
      {
        text: '',
        value: '',
        isCurrency: false,
      },
      {
        text: 'Vehicle Value for FBT',
        value: props.vehicleFBTValue,
        isCurrency: true,
      }
    )
    
    commissions.push(
      {
        text: 'Comprehensive Motor Vehicle Insurance',
        value: compMVInsValue,
        isCurrency: true,
      },
      {
        text: totalLossAssist,
        value: totalLossAssistVal,
        isCurrency: true,
      },
      {
        text: rpcText,
        value: rpcVal,
        isCurrency: true,
      },
      {
        text: safeGuardorExtended,
        value: extendedWarrantyVal,
        isCurrency: true,
      },
      {
        text: 'Lease Protection Insurance',
        value: leaseProtInsVal,
        isCurrency: true,
      },
      {
        text: 'Finance Establishment fee',
        value: financierFeeVal,
        isCurrency: true,
      },
      {
        text: 'Procurement Fee (ex GST)',
        value: procurementFee,
        isCurrency: true,
      },
      {
        text: 'Finance brokerage (ex GST)',
        value: brokerageVal,
        isCurrency: true,
      }
    )
  }

  if (globals.isBrandMX ) {
    cards.push(
      {
        text: 'Total Finance Amount',
        value: props.financeAmount,
        isCurrency: true,
        id: 'estimateSummarySection.totalfinanceamount',
      },
      {
        text: 'Term of Lease (months)',
        value: props.leaseTerm,
        isCurrency: false,
        id: 'estimateSummarySection.leaseterm',
      },
      {
        text: 'Effective Interest Rate',
        value: props.annualRate + '%',
        isCurrency: false,
        id: 'estimateSummarySection.interestrate',
      },
      {
        text: 'Vehicle Lease Repayments (per pay)',
        value: props.carRepaymentPerPayExGST,
        isCurrency: true,
        id: 'estimateSummarySection.repaymentsperpay',
      }
    )
    !isSAGBYO && cards.push({
      text: 'Total Lease and Running Costs (per pay)',
      value: props.totalLeaseRunningCostsPerPayExGST,
      isCurrency: true,
      id: 'estimateSummarySection.runningcostsperpay',
    })
    cards.push({
        text: 'Novated Lease Management Fee (per pay)',
        value: props.nlFeePerPay,
        isCurrency: true,
        id: 'estimateSummarySection.nlfeeperpay',
    })
    {isACTGO && 
    cards.push({
        text: 'FBT Statutory Rate',
        value: '20%',
        isCurrency: false,
        id: 'estimateSummarySection.statfbtrate',
      },
      {
        text: 'FBT Method',
        value: props.fbtMethod,
        isCurrency: false,
        id: 'estimateSummarySection.fbtmethod',
      },
      {
        text: 'Annual Post-tax Novated Lease Amount (ECM)',
        value: props.specm,
        isCurrency: true,
        id: 'estimateSummarySection.specm',
      },
      {
        text: 'Estimated GST on Pre-tax Payments (paid by employer)',
        value: props.preTaxPerPay / 11,
        isCurrency: true,
        id: 'estimateSummarySection.pretaxgst'
      },
      {
        text: 'Total Number of Payroll Deductions (per year)',
        value: payFrequencyToPaysPA(props.payFrequency),
        isCurrency: false,
        id: 'estimateSummarySection.paycycles',
      }
    )  
    }
    cards.push({
        text: 'Residual Percentage, as per ATO guidelines',
        value: props.residualPCT + '%',
        isCurrency: false,
        id: 'estimateSummarySection.residualpercent',
      },
      {
        text: 'Residual Value (inc GST)',
        value: props.leaseResidualAmountIncGST,
        isCurrency: true,
        id: 'estimateSummarySection.residualamount',
      },
      {
        text: 'Estimated Annual Kilometers',
        value: formatNumber(props.kmPerAnnum),
        isCurrency: false,
        id: 'estimateSummarySection.annualkm',
      },
      {
        text: 'Vehicle FBT Base Value',
        value: props.vehicleFBTValue,
        isCurrency: true,
        id: 'estimateSummarySection.fbtvalue',
      }
    )
    isSAGBYO && cards.push({
      text: 'Vehicle Lease Repayments (per pay) (2 Months Deferred)',
      value: props.monthlyRepaymentAmountIncGST * 12 / 26,
      isCurrency: true,
      id: 'estimateSummarySection.monthlyrepayment'
    })

    commissions.push(
      {
        text: 'Comprehensive Motor Vehicle Insurance',
        value: compMVInsValue,
        isCurrency: true,
        id: 'estimatesummarysectioncommissions.compinsurance',
      },
      {
        text: 'Lease Protection',
        value: leaseProtInsVal,
        id: 'estimatesummarysectioncommissions.leaseprotection',
        isCurrency: true,
      },
      {
        text: 'Guaranteed Buy Back Insurance',
        value: rpcVal,
        id: 'estimatesummarysectioncommissions.buyback',
        isCurrency: true,
      },
      {
        text: 'Total Loss Assist Insurance (Term of lease coverage)',
        value: totalLossAssistVal,
        isCurrency: true,
        id: 'estimatesummarysectioncommissions.lossassistinsurance',
      },
      {
        text: 'Extended Warranty',
        value: extendedWarrantyVal,
        isCurrency: true,
        id: 'estimatesummarysectioncommissions.extendedwarranty',
      }
    )
    if (props.employerCode === 'NSWHN' || isSAGBYO || isACTGO)
      commissions.push(
        {
          text: 'Financier Establishment Fee',
          value: financierFeeVal,
          isCurrency: true,
          id: 'estimatesummarysectioncommissions.financeestablishmentfee',
        },
        {
          text: 'Financier Brokerage',
          value: brokerageVal,
          isCurrency: true,
          id: 'estimatesummarysectioncommissions.brokerage',
        },
        {
          text: 'Procurement Fee',
          value: procurementFee,
          id: 'estimatesummarysectioncommissions.procurementfee',
          isCurrency: true,
        }
      )
  }

  return (
    <S.StyledCard pdf={props.pdf} id="estimatesummarysection">
      {!props.pdf && (
        <Card.Header className="border-bottom-0">
          <E.CardHeader>{props.title}</E.CardHeader>
        </Card.Header>
      )}
      {props.pdf && (
        <P.StyledCardHeader>
          <P.StyledCardHeaderText>{props.title}</P.StyledCardHeaderText>
        </P.StyledCardHeader>
      )}
      <Card.Body>
        <Row className="mb-3 mt-2">
          <E.SubHeadingDiv className="ml-3">LEASE DETAILS</E.SubHeadingDiv>
        </Row>
        {cards.map((ci) => (
          ci.text === '' ? <br /> :
          <RowEntry
            id={ci.text.replace(/ /g, '_')}
            title={ci.text}
            value={ci.value}
            isCurrency={ci.isCurrency}
            boldText
          />
        ))}
        
        <div>
          <hr />
          <E.SubHeadingDiv className="mb-3">
            {globals.isBrandMX
              ? (isSAGBYO ? 'MAXXIA ESTIMATED FEES AND COMMISSIONS' : 'MAXXIA ESTIMATED INSURANCE COMMISSIONS')
              : "REMSERV'S ESTIMATED COMMISSIONS"}
            {isShortLease ? ' (LEASE TERM)' : ' (ANNUALISED)'}
          </E.SubHeadingDiv>
          {commissions.map((ci) => (
            <RowEntry
              id="foo"
              title={ci.text}
              value={ci.value}
              isCurrency={true}
              boldText={globals.isBrandRS}
            />
          ))}
          <Row className="pt-3">
            <Col lg={2} md={0} xs={0}></Col>
            <Col lg={10} md={12} xs={12}>
              <RowEntry
                id="asd"
                title="Total estimated commissions"
                value={totalCommissionValue}
                isCurrency={true}
                boldValue
                boldText
              />
              <Row>
                <Col xs={12}>
                  <S.SmallText>
                    These commissions are{' '}
                    {globals.isBrandMX ? 'exclusive' : 'inclusive'} of GST unless
                    stated otherwise.
                  </S.SmallText>
                </Col>
              </Row>
            </Col>
          </Row>

          <S.SmallText className="mt-4">
            {EstimateDetailStaticData(
              (props.employerCode === 'NSWHN' || isACTGO)
                ? 'EstSummSheetFootnotev2'
                : isSAGBYO ? 'EstSummSheetFootnotev3' :
                'EstSummSheetFootnotev1'
            )}
          </S.SmallText>
        </div>
        
      </Card.Body>
    </S.StyledCard>
  )
}

export default EstimateSummarySection
